import { type ProductInfo, useProductInfo } from "@expert/contentful";
import { Center, Flex, Loader, SimpleGrid, Tabs, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { JSX } from "react";
import { useState } from "react";
import { useAnalytics } from "@/analytics";
import type { EligibleProduct } from "@/sales-tools";
import { SalesTag, useProductOfferStore } from "@/sales-tools";
import { usePartner, useSessionId } from "@/sdk";
import { useTypedMostRecentTask } from "@/sdk/agent/hooks/useMostRecentTask";
import { useFeatureFlag, userCache } from "@/shared-utils";
import { HomeProductFlow } from "../home-product/components/HomeProductFlow";
import { SaleView } from "../home-product/components/StepSpecific/SaleView";
import { EligibilityStatus, SaleProgress } from "../home-product/types";
import { SaleTrackingModal } from "./components";
import { OfferBaseCard } from "./components/BaseOfferCard";
import classes from "./MultiProduct.module.css";

export function MultiProduct(): JSX.Element | null {
    const [opened, { open, close }] = useDisclosure(false);
    const { dispatcher } = useAnalytics();
    const partner = usePartner();
    const sessionId = useSessionId();
    const { getPrimaryProducts, getOtherProducts } = useProductOfferStore();
    const [openedProduct, setOpenedProduct] = useState<EligibleProduct | undefined>(undefined);
    const primaryProducts = getPrimaryProducts(sessionId);
    const otherProducts = getOtherProducts(sessionId);
    const { data: isVerizonCareEnabled } = useFeatureFlag("isVerizonCareEnabled", { partner });
    const { isVoice } = useTypedMostRecentTask();
    const otherPlanSkuProductMap = otherProducts.reduce(
        (acc, product) => {
            acc[product.productSku] = product.id;
            return acc;
        },
        {} as Record<string, string>,
    );
    const productSKUs = Object.keys(otherPlanSkuProductMap).concat(primaryProducts.map((p) => p.productSku));
    const {
        productInfo,
        error,
        isLoading: isProductInfoLoading,
    } = useProductInfo(
        userCache.pilots.includes("care") && isVerizonCareEnabled ? "verizonCare" : partner,
        userCache.employeeId,
        productSKUs,
    );

    if (!isVoice) {
        throw new Error("Active task must be a voice task");
    }

    const eligibility = primaryProducts.map((p) => p.status);
    if (error) {
        return (
            <Text ta="center" m="xxl">
                We ran into an issue. Legal disclosures are not available right now.
            </Text>
        );
    }

    const shouldRenderProduct = (product: EligibleProduct) => {
        if (partner === "att") {
            return product.productSku !== "ATT_PA1_STATIC" && product.productSku !== "ATT_PA4_STATIC";
        }
        return (
            (userCache.pilots.includes("care") && isVerizonCareEnabled) ||
            product.productSku !== "VZN_VMPMD" ||
            otherProducts.find((p) => p.productSku === "VZN_AAL")?.saleProgress === SaleProgress.Success
        );
    };

    if (isProductInfoLoading || productSKUs.length === 0) {
        return (
            <Center h="100%" w="100%">
                <Loader />
            </Center>
        );
    }

    return (
        <Tabs
            classNames={{ root: classes.root }}
            radius={0}
            data-testid="multi-product-tab-container"
            defaultValue={!hasPrimaryProduct(productInfo!) ? "otherPlans" : "homePlan"}
        >
            <Tabs.List grow>
                {hasPrimaryProduct(productInfo!) && (
                    <Tabs.Tab
                        className={classes.tab}
                        onClick={() => void dispatcher.dispatchAction("ButtonClick", "HomeTab")}
                        value="homePlan"
                    >
                        <Flex className={classes.tabHeader} justify="center">
                            <Text size="xs">Primary</Text>{" "}
                            {eligibility.length > 0 && eligibility.includes(EligibilityStatus.Eligible) ? (
                                <SalesTag eligibility={EligibilityStatus.Eligible} />
                            ) : null}
                        </Flex>
                    </Tabs.Tab>
                )}
                {Object.keys(otherPlanSkuProductMap).length > 0 && (
                    <Tabs.Tab
                        className={classes.tab}
                        onClick={() => void dispatcher.dispatchAction("ButtonClick", "OtherPlansTab")}
                        value="otherPlans"
                    >
                        <Text size="xs">Other plans</Text>
                    </Tabs.Tab>
                )}
            </Tabs.List>
            <Tabs.Panel className={classes.tabPanel} value="homePlan">
                <HomeProductFlow />
            </Tabs.Panel>
            {Object.keys(otherPlanSkuProductMap).length > 0 && (
                <Tabs.Panel className={classes.tabPanel} value="otherPlans">
                    {otherProducts.length === 0 ? (
                        <SaleView planType="Other" status={EligibilityStatus.Ineligible} />
                    ) : (
                        <SimpleGrid className={classes.cardGrid} cols={2} spacing="lg">
                            {otherProducts.map((product) => {
                                return shouldRenderProduct(product) ? (
                                    <OfferBaseCard
                                        productInfo={productInfo?.[product.productSku]}
                                        product={product}
                                        key={product.productSku}
                                        onClick={(selected) => {
                                            void dispatcher.dispatchAction("ButtonClick", "OfferCardClicked", {
                                                productSku: selected.productSku,
                                            });
                                            setOpenedProduct(selected);
                                            open();
                                        }}
                                    />
                                ) : null;
                            })}
                        </SimpleGrid>
                    )}
                </Tabs.Panel>
            )}
            {openedProduct ? (
                <SaleTrackingModal
                    onClose={close}
                    opened={opened}
                    productInfo={productInfo?.[openedProduct.productSku]}
                    selectedProduct={openedProduct}
                    skuProductMap={otherPlanSkuProductMap}
                />
            ) : null}
        </Tabs>
    );
}

function hasPrimaryProduct(productInfo: Record<string, ProductInfo>): boolean {
    return Object.values(productInfo).some((info) => info.isPrimaryProduct);
}
